import React from 'react';
import QRCode from 'react-qr-code';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { CheckCircle2, Store, Calendar, Hash, ArrowLeft, RefreshCw } from 'lucide-react';

interface PaymentConfirmationQRProps {
  amount: string | number;
  currency: string;
  merchantName: string;
  tokenKey: string;
  transactionId?: string;
  timestamp: string;
  merchantId?: string;
  onNewPayment?: () => void;
  onGoBack?: () => void;
}

export const PaymentConfirmationQR: React.FC<PaymentConfirmationQRProps> = ({
  amount,
  currency,
  merchantName,
  tokenKey,
  transactionId,
  timestamp,
  merchantId,
  onNewPayment,
  onGoBack
}) => {
  // Create a verification payload for the QR code
  const verificationData = {
    type: 'vaiu_payment',
    tokenKey,
    transactionId,
    amount,
    currency,
    merchantId,
    timestamp,
    // Add a shortened verification code for manual entry
    verifyCode: tokenKey.substring(0, 8).toUpperCase()
  };

  const qrValue = JSON.stringify(verificationData);
  
  // Format timestamp for display
  const formatTimestamp = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="p-6">
        {/* Large Success Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-green-600">Payment Confirmed</h2>
        </div>

        {/* Large Amount Display */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 mb-6 text-center">
          <div className="text-4xl font-bold text-gray-900 dark:text-white">
            {currency} {amount}
          </div>
          <div className="text-sm text-gray-500 mt-2">PAID</div>
        </div>

        {/* Merchant Info */}
        <div className="flex items-center gap-3 mb-6 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
          <Store className="w-5 h-5 text-blue-600" />
          <div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Paid to</div>
            <div className="font-semibold text-gray-900 dark:text-white">{merchantName}</div>
          </div>
        </div>

        {/* QR Code for Verification */}
        <div className="flex flex-col items-center mb-6">
          <div className="p-4 bg-white rounded-lg shadow-sm">
            <QRCode 
              value={qrValue}
              size={200}
              level="M"
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              viewBox={`0 0 256 256`}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">Scan for merchant verification</p>
        </div>

        {/* Transaction Details */}
        <div className="space-y-3 border-t pt-4">
          {/* Verification Code - Large and Clear */}
          <div className="bg-yellow-50 dark:bg-yellow-950 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Verification Code</span>
              <span className="font-mono text-lg font-bold text-gray-900 dark:text-white">
                {tokenKey.substring(0, 8).toUpperCase()}
              </span>
            </div>
          </div>

          {/* Timestamp */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Time
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatTimestamp(timestamp)}
            </span>
          </div>

          {/* Transaction ID if available */}
          {transactionId && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <Hash className="w-4 h-4" />
                Transaction
              </span>
              <span className="font-mono text-xs text-gray-900 dark:text-white">
                {transactionId.substring(0, 12)}...
              </span>
            </div>
          )}

          {/* Full Reference */}
          <div className="text-xs text-center text-gray-500 mt-4 p-2 bg-gray-50 dark:bg-gray-900 rounded">
            Reference: {tokenKey}
          </div>
        </div>

        {/* Action Buttons */}
        {(onNewPayment || onGoBack) && (
          <div className="flex gap-3 mt-6 pt-6 border-t">
            {onNewPayment && (
              <Button
                onClick={onNewPayment}
                variant="default"
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                New Payment
              </Button>
            )}
            {onGoBack && (
              <Button
                onClick={onGoBack}
                variant="outline"
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentConfirmationQR;
